/**
 * Injection seam for every network call made by the public booking flow
 * (services → slots → appointment modal → confirmation).
 *
 * Booking components import `bookingFlowApi` / `bookingOpeningsClient` from
 * here instead of calling `apiClient` / `openingsProxyClient` directly. Each
 * method delegates to the real client unless a dev override is installed, so
 * production behavior is byte-for-byte identical.
 *
 * Why a module-level seam instead of a React context (like the dashboard's
 * `WizardActionsContext`): the desktop appointment modal is mounted in the
 * ROOT layout (`NewAppointmentModal` in `app/layout.tsx`), outside any subtree
 * a harness page could wrap in a provider. A module-level override reaches it
 * regardless of where it mounts. `setBookingFlowApiOverride` is a no-op in
 * production builds and is only called by `/dev/booking-flow`, which 404s in
 * production.
 */
import type { BookingOpeningsClient } from "@openings/template-components";
import {
  baseOk,
  handle,
  type ApiOk,
  type SendVerificationInput,
  type SendVerificationResult,
} from "@openings/api-client";
import {
  bookingConversionSchema,
  createAppointmentResponseSchema,
} from "@openings/api-contract";
import { z } from "zod";

import { apiClient } from "./openings-client";
import { openingsProxyClient } from "./openings-proxy-client";

const proxiedCreateAppointmentResponse = baseOk(
  createAppointmentResponseSchema,
);
const attributionResumeDataSchema = z
  .object({ conversion: bookingConversionSchema.nullable() })
  .strict();
const attributionResumeResponseSchema = baseOk(attributionResumeDataSchema);

export type BookingFlowAttributionResumeResult = ApiOk<
  z.infer<typeof attributionResumeDataSchema>
>;

const createAppointmentThroughWebProxy: typeof apiClient.appointments.create =
  async (input, options) => {
    const headers: Record<string, string> = {
      Accept: "application/json",
      "Content-Type": "application/json",
    };
    if (options?.idempotencyKey !== undefined) {
      if (!/^[\x21-\x7e]{1,255}$/.test(options.idempotencyKey)) {
        throw new Error(
          "Idempotency key must be 1-255 visible ASCII characters",
        );
      }
      headers["Idempotency-Key"] = options.idempotencyKey;
    }

    const response = await fetch("/api/v1/appointments", {
      method: "POST",
      headers,
      credentials: "same-origin",
      cache: "no-store",
      body: JSON.stringify(input),
    });
    return handle<
      Awaited<ReturnType<typeof apiClient.appointments.create>>
    >(response, proxiedCreateAppointmentResponse);
  };

const resumeAdsAutopilotAttributionThroughWebProxy = async (): Promise<BookingFlowAttributionResumeResult> => {
  const response = await fetch(
    "/api/v1/google/ads/autopilot/attribution/resume",
    {
      method: "POST",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
      cache: "no-store",
    },
  );
  return handle<BookingFlowAttributionResumeResult>(
    response,
    attributionResumeResponseSchema,
  );
};

export interface BookingFlowApi {
  // ── Appointment modal flow ──
  createAppointment: typeof apiClient.appointments.create;
  resumeAdsAutopilotAttribution: () => Promise<BookingFlowAttributionResumeResult>;
  rescheduleAppointment: typeof apiClient.appointments.reschedule;
  addAppointmentNotePublic: typeof apiClient.appointments.addNotePublic;
  appointmentHistory: typeof apiClient.appointments.history;
  appointmentHistoryByEmail: typeof apiClient.appointments.historyByEmail;
  verifiedAppointmentHistory: typeof apiClient.appointments.verifiedHistory;
  sendVerification: (
    input: SendVerificationInput,
  ) => Promise<SendVerificationResult>;
  getBookingGate: typeof apiClient.customers.getBookingGate;
  createCustomerForBooking: typeof apiClient.customers.createForBooking;
  createPaymentMethodForBooking: typeof apiClient.customers.createPaymentMethodForBooking;
  // ── Service requests / consultations ──
  createServiceRequest: typeof apiClient.serviceRequests.createPublic;
  uploadMediaPublic: typeof apiClient.media.uploadPublic;
  // ── Availability (goes through the /api Next proxy in production) ──
  getScheduleOpenings: BookingOpeningsClient["getScheduleOpenings"];
  getNextOpenings: BookingOpeningsClient["getNextOpenings"];
  getScheduleMemberOpenings: NonNullable<
    BookingOpeningsClient["getScheduleMemberOpenings"]
  >;
  getLocationUserDetails: NonNullable<
    BookingOpeningsClient["getLocationUserDetails"]
  >;
  getScheduleDetail: NonNullable<BookingOpeningsClient["getScheduleDetail"]>;
}

let override: Partial<BookingFlowApi> | null = null;

/**
 * Dev-only: install (or clear with `null`) a mock implementation. Hard no-op
 * in production builds — the only caller is the `/dev/booking-flow` harness.
 */
export function setBookingFlowApiOverride(
  next: Partial<BookingFlowApi> | null,
): void {
  if (process.env.NODE_ENV === "production") return;
  override = next;
}

/**
 * Dev-only: uninstall `instance`, but only while it is still the installed
 * override. A cleanup for an instance something else already replaced does
 * nothing — React can discard a tree and remount it (a hydration mismatch),
 * running the discarded tree's cleanup *after* the new tree installed its own
 * mock, and dropping that one left the harness reading the real API. It
 * answers 502 locally, which the booking page shows as "No openings
 * available".
 *
 * **Call this from a layout-effect cleanup, never a passive one** — see
 * `useMockBookingFlowApi`. Identity alone cannot tell strict mode's replay
 * (cleanup → descendants reconnect their mount effects → the installer
 * reinstalls, all with the SAME instance) from a real unmount, and the clear
 * is synchronous, so a passive cleanup would uninstall the mock for exactly
 * the descendants the harness exists to serve. React settles the layout phase
 * — including strict mode's replay of it — before any passive effect runs, so
 * from a layout cleanup both cases land correctly: the replay reinstalls
 * before descendants ask, and a real departure restores the real client
 * before the replacement tree's effects run.
 */
export function clearBookingFlowApiOverride(
  instance: Partial<BookingFlowApi>,
): void {
  if (process.env.NODE_ENV === "production") return;
  if (override !== instance) return;
  override = null;
}

export const bookingFlowApi: BookingFlowApi = {
  createAppointment: (input, options) =>
    (override?.createAppointment ??
      createAppointmentThroughWebProxy)(
      input,
      options,
    ),
  resumeAdsAutopilotAttribution: () =>
    (override?.resumeAdsAutopilotAttribution ??
      resumeAdsAutopilotAttributionThroughWebProxy)(),
  rescheduleAppointment: (input) =>
    (override?.rescheduleAppointment ??
      apiClient.appointments.reschedule.bind(apiClient.appointments))(input),
  addAppointmentNotePublic: (input) =>
    (override?.addAppointmentNotePublic ??
      apiClient.appointments.addNotePublic.bind(apiClient.appointments))(input),
  appointmentHistory: (input) =>
    (override?.appointmentHistory ??
      apiClient.appointments.history.bind(apiClient.appointments))(input),
  appointmentHistoryByEmail: (input) =>
    (override?.appointmentHistoryByEmail ??
      apiClient.appointments.historyByEmail.bind(apiClient.appointments))(
      input,
    ),
  verifiedAppointmentHistory: (input) =>
    (override?.verifiedAppointmentHistory ??
      apiClient.appointments.verifiedHistory.bind(apiClient.appointments))(
      input,
    ),
  sendVerification: (input) =>
    (override?.sendVerification ??
      apiClient.verifications.send.bind(apiClient.verifications))(input),
  getBookingGate: (input) =>
    (override?.getBookingGate ??
      apiClient.customers.getBookingGate.bind(apiClient.customers))(input),
  createCustomerForBooking: (input) =>
    (override?.createCustomerForBooking ??
      apiClient.customers.createForBooking.bind(apiClient.customers))(input),
  createPaymentMethodForBooking: (input) =>
    (override?.createPaymentMethodForBooking ??
      apiClient.customers.createPaymentMethodForBooking.bind(
        apiClient.customers,
      ))(input),
  createServiceRequest: (input) =>
    (override?.createServiceRequest ??
      apiClient.serviceRequests.createPublic.bind(apiClient.serviceRequests))(
      input,
    ),
  uploadMediaPublic: (input) =>
    (override?.uploadMediaPublic ??
      apiClient.media.uploadPublic.bind(apiClient.media))(input),
  getScheduleOpenings: (input) =>
    (override?.getScheduleOpenings ??
      openingsProxyClient.openings.getScheduleOpenings.bind(
        openingsProxyClient.openings,
      ))(input),
  getNextOpenings: (input) =>
    (override?.getNextOpenings ??
      openingsProxyClient.openings.getNextOpenings.bind(
        openingsProxyClient.openings,
      ))(input),
  getScheduleMemberOpenings: (input) =>
    (override?.getScheduleMemberOpenings ??
      openingsProxyClient.openings.getScheduleMemberOpenings.bind(
        openingsProxyClient.openings,
      ))(input),
  getLocationUserDetails: (userId) =>
    (override?.getLocationUserDetails ??
      openingsProxyClient.openings.getLocationUserDetails.bind(
        openingsProxyClient.openings,
      ))(userId),
  getScheduleDetail: (scheduleId) =>
    override?.getScheduleDetail
      ? override.getScheduleDetail(scheduleId)
      : (openingsProxyClient.locations.getDetail(scheduleId) as ReturnType<
          NonNullable<BookingOpeningsClient["getScheduleDetail"]>
        >),
};

/**
 * `BookingOpeningsClient`-shaped view over the seam, used everywhere the
 * booking UI needs an openings client (schedule AND member modes).
 */
export const bookingOpeningsClient: BookingOpeningsClient = {
  get photoTrackingEnabled() { return !override; },
  getScheduleOpenings: bookingFlowApi.getScheduleOpenings,
  getNextOpenings: bookingFlowApi.getNextOpenings,
  getScheduleMemberOpenings: bookingFlowApi.getScheduleMemberOpenings,
  getLocationUserDetails: bookingFlowApi.getLocationUserDetails,
  getScheduleDetail: bookingFlowApi.getScheduleDetail,
};
